import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

// ── Multi-select option lists ─────────────────────────────────
const AUTOMATIC_TOW_REASONS = [
  'No Parking Permit',
  'Parked in Handicap without placard or plate',
  'Parked in Fire Lane',
  'Parked in Striped Out Area',
  'Occupying Multiple Spaces',
  'Parked on Grass or Sidewalks',
  'Blocking Garage/Drive/Exit',
  'Blocking Dumpster',
  'Resident Must Park in Resident',
  'Visitor Must Park in Visitors',
  'Patrol Reserved Parking',
  'Unregistered Visitor',
  'Office Parking',
];

const WARNING_VIOLATIONS = [
  'Flat Tires',
  'Leaking Fluids',
  'Immobile',
  'Expired Registration',
  'Mechanical Work',
  'Blocks/Jacks',
  'Wrecked/Junk',
];

const SERVICE_TYPES = [
  'My Community VIP',
  'Resident Parking Permit',
  'Printer Permit',
  'Permit Parking',
  'R2PARK.COM',
  'Commercial Property',
];

const NOTICE_OPTIONS = [
  'English',
  'Spanish',
  'R2PARK',
  'Permit Information Sheet',
];

const SIGN_OPTIONS = [
  'English',
  'Spanish',
  'R2PARK',
];

const STORAGE_FACILITIES = [
  "Papa's Lot Fort Worth",
  "Papa's Lot Denton",
  'Sure Storage Mesquite',
  'Sure Storage Dallas',
  'Peak North',
  'Peak South',
];

// ── Component ─────────────────────────────────────────────────
export default function PropertyInfoSheetPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('account_id');
  const fileRef = useRef();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [existingId, setExistingId] = useState(null);
  const [mapFile, setMapFile] = useState(null);
  const [existingMap, setExistingMap] = useState(null);

  const [form, setForm] = useState({
    property_name: '', management_company: '', property_address: '', number_of_units: '',
    manager_name: '', manager_email: '', assistant_manager_name: '', assistant_manager_email: '',
    property_phone: '', authorized_callers: [''], password: '', gate_code: '',
    patrol_hours_start: '', patrol_hours_end: '',
    patrol_hours_am_pm_start: 'PM', patrol_hours_am_pm_end: 'AM',
    patrol_24_7: false,
    automatic_tow_reasons: [], warning_violations: [], service_types: [],
    printer_permit_color: '', permit_type: '',
    special_instructions: '',
    notices: [], signs: [],
    zone: '', storage_facility: '', handicap_spaces: '',
    tow_start_date: '', notice_1_date: '', notice_2_date: '', notice_3_date: '',
    cancellation_notice_to: '',
  });

  // Load existing sheet if linked to account
  useEffect(() => {
    if (!accountId) return;
    api.getPropertyInfoByAccount(accountId).then(sheet => {
      if (sheet) {
        setExistingId(sheet.id);
        if (sheet.map_file_path) {
          setExistingMap({ path: sheet.map_file_path, name: sheet.map_original_name });
        }
        const loaded = {};
        for (const [k, v] of Object.entries(form)) {
          if (k === 'patrol_24_7') {
            loaded[k] = !!sheet[k];
          } else if (k === 'authorized_callers') {
            const arr = Array.isArray(sheet[k]) ? sheet[k] : [];
            loaded[k] = arr.length > 0 ? arr : [''];
          } else if (Array.isArray(v)) {
            loaded[k] = Array.isArray(sheet[k]) ? sheet[k] : [];
          } else {
            loaded[k] = sheet[k] ?? '';
          }
        }
        setForm(loaded);
      }
    }).catch(() => {});
    // Also pre-fill from account data
    api.getAccount(accountId).then(acct => {
      if (acct) {
        setForm(f => ({
          ...f,
          property_name: f.property_name || acct.property_name || '',
          property_address: f.property_address || [acct.address, acct.city, acct.state, acct.zip].filter(Boolean).join(', ') || '',
          manager_name: f.manager_name || acct.contact_name || '',
          manager_email: f.manager_email || acct.contact_email || '',
          property_phone: f.property_phone || acct.contact_phone || '',
        }));
      }
    }).catch(() => {});
  }, [accountId]); // eslint-disable-line

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function toggleMulti(field, value) {
    setForm(f => {
      const arr = f[field] || [];
      return { ...f, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  }

  // Authorized callers — add/remove/update
  function addCaller() { setForm(f => ({ ...f, authorized_callers: [...f.authorized_callers, ''] })); }
  function removeCaller(idx) { setForm(f => ({ ...f, authorized_callers: f.authorized_callers.filter((_, i) => i !== idx) })); }
  function updateCaller(idx, val) { setForm(f => ({ ...f, authorized_callers: f.authorized_callers.map((c, i) => i === idx ? val : c) })); }

  // 24/7 patrol toggle
  function toggle247() {
    setForm(f => {
      if (!f.patrol_24_7) {
        return { ...f, patrol_24_7: true, patrol_hours_start: '', patrol_hours_end: '', patrol_hours_am_pm_start: 'PM', patrol_hours_am_pm_end: 'AM' };
      }
      return { ...f, patrol_24_7: false };
    });
  }

  // Validation
  function validate() {
    const required = [
      ['property_name', 'Property Name'],
      ['management_company', 'Management Company'],
      ['property_address', 'Property Address'],
      ['number_of_units', 'Number of Units'],
      ['manager_name', 'Property Manager Name'],
      ['manager_email', 'Manager Email'],
      ['assistant_manager_name', 'Assistant Manager Name'],
      ['assistant_manager_email', 'Assistant Manager Email'],
      ['property_phone', 'Property Phone Number'],
      ['password', 'Password'],
      ['gate_code', 'Gate Code'],
      ['special_instructions', 'Special Instructions'],
      ['handicap_spaces', 'Number of Handicap Spaces'],
      ['storage_facility', 'Storage Facility'],
      ['tow_start_date', 'Tow Start Date'],
      ['notice_1_date', 'Date of 1st Notice'],
      ['notice_2_date', 'Date of 2nd Notice'],
      ['notice_3_date', 'Date of 3rd Notice'],
      ['cancellation_notice_to', 'Cancellation Notice Recipient'],
    ];

    for (const [field, label] of required) {
      if (!form[field]?.toString().trim()) return `${label} is required`;
    }

    // Authorized callers — at least one non-empty
    const callers = form.authorized_callers.filter(c => c.trim());
    if (callers.length === 0) return 'At least one Authorized Caller is required';

    // Patrol hours — either 24/7 or both times filled
    if (!form.patrol_24_7) {
      if (!form.patrol_hours_start || !form.patrol_hours_end) return 'Patrol hours are required (select times or 24/7)';
    }

    // Multi-select required
    if (form.automatic_tow_reasons.length === 0) return 'Select at least one Reason for Automatic Tow';
    if (form.warning_violations.length === 0) return 'Select at least one Warning Violation';
    if (form.service_types.length === 0) return 'Select at least one Service Type';
    if (form.notices.length === 0) return 'Select at least one Notice type';
    if (form.signs.length === 0) return 'Select at least one Sign type';

    // Conditional permit fields
    const needsPermitFields = form.service_types.includes('Printer Permit') || form.service_types.includes('Permit Parking');
    if (needsPermitFields) {
      if (!form.printer_permit_color.trim()) return 'Permit Color is required when Printer Permit or Permit Parking is selected';
      if (!form.permit_type.trim()) return 'Permit Type is required when Printer Permit or Permit Parking is selected';
    }

    // Map required
    if (!mapFile && !existingMap) return 'Property Map is required';

    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const fd = new FormData();
      if (accountId) fd.append('account_id', accountId);

      for (const [k, v] of Object.entries(form)) {
        if (k === 'patrol_24_7') {
          fd.append(k, v ? '1' : '0');
        } else if (Array.isArray(v)) {
          fd.append(k, JSON.stringify(v));
        } else {
          fd.append(k, v);
        }
      }

      if (mapFile) {
        fd.append('map_file', mapFile);
      }

      const result = await api.savePropertyInfo(fd);
      setExistingId(result.id);
      setSuccess('Property information sheet saved successfully!');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSubmitting(false);
  }

  const showPermitFields = form.service_types.includes('Printer Permit') || form.service_types.includes('Permit Parking');

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {accountId && (
            <button onClick={() => navigate(`/sales/accounts/${accountId}`)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#64748b' }}>
              &larr;
            </button>
          )}
          <h1 style={{ margin: 0 }}>Property Information Sheet</h1>
          {existingId && (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: '#dcfce7', color: '#16a34a' }}>
              Saved
            </span>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', color: '#16a34a', fontSize: 13, marginBottom: 16 }}>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Sales Rep — hardcoded */}
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 16px', marginBottom: 20, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Sales Rep:</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{user?.full_name || user?.username}</span>
        </div>

        {/* ── Section 1: Property Details ── */}
        <Section title="Property Details">
          <Row>
            <Field label="Property Name" required>
              <input className="sales-input" value={form.property_name} onChange={e => set('property_name', e.target.value)} />
            </Field>
            <Field label="Management Company" required>
              <input className="sales-input" value={form.management_company} onChange={e => set('management_company', e.target.value)} />
            </Field>
          </Row>
          <Row>
            <Field label="Property Address" required full>
              <input className="sales-input" value={form.property_address} onChange={e => set('property_address', e.target.value)} />
            </Field>
          </Row>
          <Row>
            <Field label="Number of Units" required>
              <input className="sales-input" value={form.number_of_units} onChange={e => set('number_of_units', e.target.value)} />
            </Field>
            <Field label="Number of Handicap Spaces" required>
              <input className="sales-input" value={form.handicap_spaces} onChange={e => set('handicap_spaces', e.target.value)} />
            </Field>
          </Row>
        </Section>

        {/* ── Section 2: Contacts ── */}
        <Section title="Contacts">
          <Row>
            <Field label="Property Manager Name" required>
              <input className="sales-input" value={form.manager_name} onChange={e => set('manager_name', e.target.value)} />
            </Field>
            <Field label="Manager Email" required>
              <input className="sales-input" type="email" value={form.manager_email} onChange={e => set('manager_email', e.target.value)} />
            </Field>
          </Row>
          <Row>
            <Field label="Assistant Manager Name" required>
              <input className="sales-input" value={form.assistant_manager_name} onChange={e => set('assistant_manager_name', e.target.value)} />
            </Field>
            <Field label="Assistant Manager Email" required>
              <input className="sales-input" type="email" value={form.assistant_manager_email} onChange={e => set('assistant_manager_email', e.target.value)} />
            </Field>
          </Row>
          <Row>
            <Field label="Property Phone Number" required>
              <input className="sales-input" value={form.property_phone} onChange={e => set('property_phone', e.target.value)} />
            </Field>
          </Row>

          {/* Authorized Callers — dynamic add another */}
          <div style={{ marginTop: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>
              Authorized Callers <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {form.authorized_callers.map((caller, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="sales-input" style={{ flex: 1 }}
                    value={caller}
                    onChange={e => updateCaller(idx, e.target.value)}
                    placeholder={`Caller ${idx + 1}`}
                  />
                  {form.authorized_callers.length > 1 && (
                    <button type="button" onClick={() => removeCaller(idx)}
                      style={{
                        background: 'none', border: '1px solid #fecaca', borderRadius: 8,
                        color: '#dc2626', cursor: 'pointer', padding: '8px 12px', fontSize: 14,
                        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                      &times;
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addCaller}
              style={{
                marginTop: 8, background: 'none', border: '1px dashed #cbd5e1', borderRadius: 8,
                color: '#3b82f6', cursor: 'pointer', padding: '8px 16px', fontSize: 13, fontWeight: 600,
                width: '100%', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#eff6ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = 'none'; }}
            >
              + Add Another Caller
            </button>
          </div>
        </Section>

        {/* ── Section 3: Access & Security ── */}
        <Section title="Access & Security">
          <Row>
            <Field label="Password" required>
              <input className="sales-input" value={form.password} onChange={e => set('password', e.target.value)} />
            </Field>
            <Field label="Gate Code" required>
              <input className="sales-input" value={form.gate_code} onChange={e => set('gate_code', e.target.value)} />
            </Field>
          </Row>
        </Section>

        {/* ── Section 4: Patrol Hours ── */}
        <Section title="Hours for Patrol" required>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <button type="button" onClick={toggle247}
              style={{
                padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                cursor: 'pointer', transition: 'all 0.2s',
                border: form.patrol_24_7 ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                background: form.patrol_24_7 ? '#3b82f6' : '#fff',
                color: form.patrol_24_7 ? '#fff' : '#0f172a',
              }}>
              24/7
            </button>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              {form.patrol_24_7 ? 'Patrol runs 24 hours, 7 days a week' : 'Or select specific hours below'}
            </span>
          </div>

          {!form.patrol_24_7 && (
            <Row>
              <Field label="Start Time" required>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="sales-input" style={{ flex: 1 }} type="time" value={form.patrol_hours_start} onChange={e => set('patrol_hours_start', e.target.value)} />
                  <select className="sales-input" style={{ width: 70, flex: 'none' }} value={form.patrol_hours_am_pm_start} onChange={e => set('patrol_hours_am_pm_start', e.target.value)}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </Field>
              <Field label="End Time" required>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="sales-input" style={{ flex: 1 }} type="time" value={form.patrol_hours_end} onChange={e => set('patrol_hours_end', e.target.value)} />
                  <select className="sales-input" style={{ width: 70, flex: 'none' }} value={form.patrol_hours_am_pm_end} onChange={e => set('patrol_hours_am_pm_end', e.target.value)}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </Field>
            </Row>
          )}
        </Section>

        {/* ── Section 5: Automatic Tow Reasons ── */}
        <Section title="Reasons for Automatic Tow" required>
          <CheckboxGrid options={AUTOMATIC_TOW_REASONS} selected={form.automatic_tow_reasons} onToggle={v => toggleMulti('automatic_tow_reasons', v)} />
        </Section>

        {/* ── Section 6: Warning Violations ── */}
        <Section title="Warning Violations" required>
          <CheckboxGrid options={WARNING_VIOLATIONS} selected={form.warning_violations} onToggle={v => toggleMulti('warning_violations', v)} />
        </Section>

        {/* ── Section 7: Service Type ── */}
        <Section title="Service Type" required>
          <CheckboxGrid options={SERVICE_TYPES} selected={form.service_types} onToggle={v => toggleMulti('service_types', v)} />
          {showPermitFields && (
            <div style={{ marginTop: 14, padding: 16, borderRadius: 10, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <Row>
                <Field label="Permit Color" required>
                  <input className="sales-input" value={form.printer_permit_color} onChange={e => set('printer_permit_color', e.target.value)} placeholder="e.g. Red, Blue, Green" />
                </Field>
                <Field label="Permit Type" required>
                  <input className="sales-input" value={form.permit_type} onChange={e => set('permit_type', e.target.value)} placeholder="e.g. Hang Tag, Sticker, Decal" />
                </Field>
              </Row>
            </div>
          )}
        </Section>

        {/* ── Section 8: Special Instructions ── */}
        <Section title="Special Instructions" required>
          <textarea className="sales-input" style={{ minHeight: 100, resize: 'vertical' }} value={form.special_instructions} onChange={e => set('special_instructions', e.target.value)}
            placeholder="Any special instructions for this property..." />
        </Section>

        {/* ── Section 9: Notices ── */}
        <Section title="Notices" required>
          <CheckboxGrid options={NOTICE_OPTIONS} selected={form.notices} onToggle={v => toggleMulti('notices', v)} />
        </Section>

        {/* ── Section 10: Signs ── */}
        <Section title="Signs" required>
          <CheckboxGrid options={SIGN_OPTIONS} selected={form.signs} onToggle={v => toggleMulti('signs', v)} />
        </Section>

        {/* ── Section 11: Property Map ── */}
        <Section title="Property Map" required>
          <div
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#3b82f6'; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
            onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#e2e8f0'; if (e.dataTransfer.files[0]) setMapFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            style={{
              border: '2px dashed #e2e8f0', borderRadius: 10, padding: 24,
              textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s',
            }}
          >
            {mapFile ? (
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{mapFile.name}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{(mapFile.size / 1024).toFixed(0)} KB — click to change</div>
              </div>
            ) : existingMap ? (
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{existingMap.name}</div>
                <div style={{ fontSize: 12, color: '#10b981', marginTop: 2 }}>Previously uploaded — click to replace</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 22, marginBottom: 6 }}>&#128506;</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Upload Property Map</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Max 10 MB</div>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) setMapFile(e.target.files[0]); }} />
          </div>
        </Section>

        {/* ── Section 12: Zone & Storage ── */}
        <Section title="Assignment">
          <Row>
            <Field label="Zone">
              <input className="sales-input" value={form.zone} onChange={e => set('zone', e.target.value)} />
            </Field>
            <Field label="Storage Facility" required>
              <select className="sales-input" value={form.storage_facility} onChange={e => set('storage_facility', e.target.value)}>
                <option value="">Select facility...</option>
                {STORAGE_FACILITIES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
          </Row>
        </Section>

        {/* ── Section 13: Dates ── */}
        <Section title="Key Dates">
          <Row>
            <Field label="Tow Start Date" required>
              <input className="sales-input" type="date" value={form.tow_start_date} onChange={e => set('tow_start_date', e.target.value)} />
            </Field>
            <Field label="Date of 1st Notice" required>
              <input className="sales-input" type="date" value={form.notice_1_date} onChange={e => set('notice_1_date', e.target.value)} />
            </Field>
          </Row>
          <Row>
            <Field label="Date of 2nd Notice" required>
              <input className="sales-input" type="date" value={form.notice_2_date} onChange={e => set('notice_2_date', e.target.value)} />
            </Field>
            <Field label="Date of 3rd Notice" required>
              <input className="sales-input" type="date" value={form.notice_3_date} onChange={e => set('notice_3_date', e.target.value)} />
            </Field>
          </Row>
        </Section>

        {/* ── Section 14: Cancellation ── */}
        <Section title="Cancellation Notice">
          <Field label="Who do we send Cancellation Notice to?" required>
            <input className="sales-input" value={form.cancellation_notice_to} onChange={e => set('cancellation_notice_to', e.target.value)}
              placeholder="Name and/or email" />
          </Field>
        </Section>

        {/* Submit */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          {accountId && (
            <button type="button" className="btn btn-secondary" onClick={() => navigate(`/sales/accounts/${accountId}`)}>
              Back to Account
            </button>
          )}
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : existingId ? 'Update Info Sheet' : 'Save Info Sheet'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Reusable layout components ────────────────────────────────
function Section({ title, required, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
        {title}
        {required && <span style={{ color: '#dc2626', fontSize: 12 }}> *</span>}
      </h3>
      {children}
    </div>
  );
}

function Row({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 14 }}>
      {children}
    </div>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : {}}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function CheckboxGrid({ options, selected, onToggle }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
      {options.map(opt => (
        <label key={opt} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
          borderRadius: 8, border: `1px solid ${selected.includes(opt) ? '#3b82f6' : '#e2e8f0'}`,
          background: selected.includes(opt) ? '#eff6ff' : '#fff',
          cursor: 'pointer', fontSize: 13, transition: 'all 0.15s',
        }}>
          <input type="checkbox" checked={selected.includes(opt)} onChange={() => onToggle(opt)}
            style={{ accentColor: '#3b82f6', width: 16, height: 16 }} />
          <span style={{ color: '#0f172a', fontWeight: selected.includes(opt) ? 600 : 400 }}>{opt}</span>
        </label>
      ))}
    </div>
  );
}
